import { gql, useMutation } from "@apollo/client";
import {
  Card,
  CircularProgress,
  circularProgressClasses,
  Collapse,
  Fade,
  Grid,
  Icon,
  Modal,
  Tooltip,
} from "@mui/material";
import ModalImage from "components/Modals/ModalImage";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useContext, useState } from "react";
import { API_URL } from "services/config";
import { getColor } from "utils/colors";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "50%", xxl: "50%" },
  overflowY: "auto",
};

export default function ModalPermisosCampo({ open, handleClose, archivos, refetch }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const { confirmar } = useContext(ConfirmarContext);
  const { handleSnackbar } = useContext(MessageManager);

  const [updatePermisoFileStatus, { loading: loadingUpdatePermisoFileStatus }] = useMutation(
    gql`
      mutation updatePermisoFileStatus($id: ID!, $estado: String!, $motivoRechazo: String) {
        updatePermisoFileStatus(id: $id, estado: $estado, motivoRechazo: $motivoRechazo) {
          id
        }
      }
    `
  );

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" p={3} pb={0}>
              <SoftBox>
                <SoftTypography variant="h6">Permisos de campo</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
                <SoftBox display="flex" alignItems="center">
                  <Tooltip title="Cerrar" placement="top">
                    <Icon
                      fontSize="medium"
                      onClick={() => {
                        handleClose();
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox p={3}>
              <Grid container spacing={2}>
                {archivos.map((archivo, index) => (
                  <Grid item xs={12} key={archivo.id}>
                    <Card
                      sx={{
                        border: "1px solid rgba(0, 0, 0, 0.12)",
                      }}
                    >
                      <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        px={2}
                        py={1}
                        sx={{
                          borderBottom:
                            expanded !== archivo.id ? "none" : "1px solid rgba(0, 0, 0, 0.12)",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                        onClick={() => setExpanded(expanded === archivo.id ? null : archivo.id)}
                      >
                        <SoftBox display="flex" alignItems="center">
                          <SoftBox
                            mr={1}
                            sx={{
                              display: {
                                xs: "none",
                                sm: "flex",
                              },
                            }}
                          >
                            <Tooltip title={archivo.tipo} placement="top">
                              <Icon
                                sx={{
                                  color: getColor("gobSecondary"),
                                }}
                              >
                                file_present
                              </Icon>
                            </Tooltip>
                          </SoftBox>
                          <SoftBox display="flex" flexDirection="column">
                            <SoftBox display="flex" alignItems="center" gap={1}>
                              <SoftTypography variant="h6" fontWeight="bold">
                                {archivo?.nombre}
                              </SoftTypography>
                              <SoftBadge
                                color={
                                  archivo?.estado === "Aprobado"
                                    ? "success"
                                    : archivo?.estado === "Rechazado"
                                    ? "error"
                                    : "warning"
                                }
                                badgeContent={archivo?.estado}
                              />
                            </SoftBox>
                            <SoftTypography variant="caption" fontWeight="regular">
                              {`Cargado por ${archivo?.usuario?.nombre} ${
                                archivo?.usuario?.apellido
                              } el ${dayjs(archivo?.createdAt).format("DD/MM/YYYY")} a las ${dayjs(
                                archivo?.createdAt
                              ).format("HH:mm")}`}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                        <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
                          <SoftBox>
                            <Tooltip
                              title={expanded === archivo.id ? "Ocultar" : "Mostrar"}
                              placement="top"
                            >
                              <SoftButton
                                color="primary"
                                iconOnly
                                onClick={() =>
                                  setExpanded(expanded === archivo.id ? null : archivo.id)
                                }
                              >
                                <Icon
                                  sx={{
                                    rotate: expanded === archivo.id ? "180deg" : "0deg",
                                    transition: "all 0.5s",
                                  }}
                                >
                                  expand_more
                                </Icon>
                              </SoftButton>
                            </Tooltip>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                      <Collapse in={expanded === archivo.id}>
                        {archivo?.estado === "Rechazado" && (
                          <SoftBox>
                            <SoftBox display="flex" flexDirection="column" p={2}>
                              <SoftTypography variant="caption">Motivo del rechazo</SoftTypography>
                              <SoftTypography variant="h6">{archivo?.motivoRechazo}</SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        )}
                        {archivo?.file?.includes(".pdf") ? (
                          <SoftBox p={2}>
                            <SoftButton
                              color="primary"
                              fullWidth
                              onClick={() => {
                                window.open(`${API_URL}/${archivo?.file}`, "_blank");
                              }}
                            >
                              Abrir pdf &nbsp;
                              <Icon>arrow_forward</Icon>
                            </SoftButton>
                          </SoftBox>
                        ) : (
                          <SoftBox
                            component="img"
                            src={`${API_URL}/${archivo?.file}`}
                            alt="Archivo original"
                            sx={{
                              width: "100%",
                              overflowY: "auto",
                              "&:hover": {
                                cursor: "pointer",
                                opacity: 0.8,
                              },
                            }}
                            onClick={() => setSelectedImage(`${API_URL}/${archivo?.file}`)}
                          />
                        )}
                        <SoftBox display="flex" gap={2} p={2} pt={0}>
                          {archivo?.estado !== "Rechazado" && (
                            <SoftBox
                              sx={{
                                width: "100%",
                              }}
                            >
                              <SoftButton
                                color="error"
                                fullWidth
                                onClick={() => {
                                  confirmar({
                                    title: "Rechazar permiso de campo",
                                    pedirMotivo: true,
                                    message: (
                                      <SoftTypography variant="h6" fontWeight="light">
                                        ¿Está seguro que desea rechazar el{" "}
                                        <span style={{ fontWeight: "bold" }}>permiso de campo</span>{" "}
                                        <span
                                          style={{ fontWeight: "bold" }}
                                        >{`${archivo?.nombre}`}</span>
                                        ?
                                      </SoftTypography>
                                    ),
                                    icon: "warning",
                                    func: (motivo) => {
                                      updatePermisoFileStatus({
                                        variables: {
                                          id: archivo.id,
                                          estado: "Rechazado",
                                          motivoRechazo: motivo,
                                        },
                                      })
                                        .then(() => {
                                          refetch();
                                          handleSnackbar(
                                            "Archivo rechazado correctamente",
                                            "success"
                                          );
                                        })
                                        .catch((err) => {
                                          handleSnackbar(
                                            err.message ||
                                              "Ha ocurrido un error al actualizar el estado",
                                            "error"
                                          );
                                        });
                                    },
                                  });
                                }}
                                disabled={loadingUpdatePermisoFileStatus}
                              >
                                Rechazar
                                {loadingUpdatePermisoFileStatus ? (
                                  <CircularProgress
                                    size={15}
                                    sx={{
                                      marginRight: 0.5,
                                    }}
                                    color="inherit"
                                  />
                                ) : (
                                  <Icon sx={{ fontWeight: "light" }}>close</Icon>
                                )}
                              </SoftButton>
                            </SoftBox>
                          )}
                          {archivo?.estado !== "Aprobado" && (
                            <SoftBox
                              sx={{
                                width: "100%",
                              }}
                            >
                              <SoftButton
                                color="success"
                                fullWidth
                                onClick={() => {
                                  updatePermisoFileStatus({
                                    variables: {
                                      id: archivo.id,
                                      estado: "Aprobado",
                                    },
                                  })
                                    .then(() => {
                                      refetch();
                                      handleSnackbar("Archivo aprobado correctamente", "success");
                                    })
                                    .catch((err) => {
                                      handleSnackbar(
                                        err.message ||
                                          "Ha ocurrido un error al actualizar el estado",
                                        "error"
                                      );
                                    });
                                }}
                                disabled={loadingUpdatePermisoFileStatus}
                              >
                                Aprobar
                                {loadingUpdatePermisoFileStatus ? (
                                  <CircularProgress
                                    size={15}
                                    sx={{
                                      marginRight: 0.5,
                                    }}
                                    color="inherit"
                                  />
                                ) : (
                                  <Icon sx={{ fontWeight: "light" }}>check</Icon>
                                )}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </Collapse>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </SoftBox>
            <ModalImage
              open={selectedImage !== null}
              handleClose={() => setSelectedImage(null)}
              src={selectedImage}
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalPermisosCampo.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  archivos: PropTypes.array,
  refetch: PropTypes.func,
};
